import React, { createContext, useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ECOMMERCE_BRANDING } from './branding';
import { getAppBase, normalizeAppUrl } from '../utils/styleHelpers';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children, config }) => {
    const [cartItems, setCartItems] = useState([]);
    const [addedToast, setAddedToast] = useState(null);
    const settings = config?.settings || {};
    const isCommercialMode = Boolean(config?.styleSettings?.shopCommercialOrderForm || settings.shopCommercialOrderForm || settings.cartCommercialMode);

    // Load cart from localStorage
    useEffect(() => {
        const stored = localStorage.getItem(ECOMMERCE_BRANDING.cartStorageKey);
        if (stored) {
            try {
                setCartItems(JSON.parse(stored));
            } catch (e) {
                console.error("Failed to parse cart items", e);
            }
        }
    }, []);

    // Auto-hide toast notification after 4 seconds
    useEffect(() => {
        if (addedToast) {
            const timer = setTimeout(() => setAddedToast(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [addedToast]);

    // Save cart to localStorage
    const saveCart = (items) => {
        setCartItems(items);
        localStorage.setItem(ECOMMERCE_BRANDING.cartStorageKey, JSON.stringify(items));
    };

    const getStockNotice = (stock) => {
        const template = settings.outOfStockNotice || ECOMMERCE_BRANDING.outOfStockNotice || "Sorry, only {stock} items are in stock.";
        return template.replace('{stock}', stock);
    };

    const isTrueInquiry = (val) => {
        if (val === true || val === 1 || val === '1' || val === 'true') return true;
        return false;
    };

    const addBatchToCart = (itemsToBatch) => {
        if (!Array.isArray(itemsToBatch) || itemsToBatch.length === 0) return 0;

        let totalItemsAdded = 0;
        let itemsCount = 0;

        setCartItems(prevCartItems => {
            let updatedCart = [...prevCartItems];

            for (const entry of itemsToBatch) {
                const { product, quantity = 1, selectedAttributes = null, variation = null } = entry;
                if (!product || (!isCommercialMode && isTrueInquiry(product.inquiryOnly))) continue;

                const qtyToAdd = parseInt(quantity, 10);
                if (isNaN(qtyToAdd) || qtyToAdd <= 0) continue;

                const currentPrice = variation?.price !== undefined && variation.price > 0
                    ? parseFloat(variation.price)
                    : (parseFloat(product.price) || 0);

                const currentStock = variation?.stock !== undefined ? parseInt(variation.stock, 10) : product.stock;
                const parsedStock = (currentStock !== undefined && currentStock !== null && currentStock !== '')
                    ? parseInt(currentStock, 10)
                    : 999;
                
                let cartItemId = product.id;
                if (variation?.id) {
                    cartItemId = `${product.id}_${variation.id}`;
                } else if (selectedAttributes && Object.keys(selectedAttributes).length > 0) {
                    const attrKey = Object.entries(selectedAttributes).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}:${v}`).join('|');
                    cartItemId = `${product.id}_${attrKey}`;
                }

                const existingIdx = updatedCart.findIndex(item => item.cartItemId === cartItemId || item.id === cartItemId || (!item.cartItemId && !variation && item.id === product.id));
                const existingQty = existingIdx >= 0 ? updatedCart[existingIdx].quantity : 0;
                const targetQty = existingQty + qtyToAdd;

                if (!isCommercialMode && !isNaN(parsedStock) && parsedStock > 0 && targetQty > parsedStock) {
                    continue;
                }

                const itemImage = (variation?.image) || (product.images && product.images[0]) || '';
                const normalizedProduct = {
                    ...product,
                    id: cartItemId,
                    cartItemId,
                    originalProductId: product.id,
                    price: currentPrice,
                    originalPrice: parseFloat(product.price) || 0,
                    stock: currentStock,
                    sku: variation?.sku || product.sku || '',
                    images: itemImage ? [itemImage, ...(product.images || []).filter(img => img !== itemImage)] : (product.images || []),
                    selectedAttributes: selectedAttributes || variation?.attributes || null,
                    variationId: variation?.id || null
                };

                if (existingIdx >= 0) {
                    updatedCart[existingIdx] = {
                        ...updatedCart[existingIdx],
                        quantity: targetQty,
                        price: currentPrice
                    };
                } else {
                    updatedCart.push({
                        ...normalizedProduct,
                        quantity: qtyToAdd
                    });
                }

                totalItemsAdded += qtyToAdd;
                itemsCount++;
            }

            localStorage.setItem(ECOMMERCE_BRANDING.cartStorageKey, JSON.stringify(updatedCart));
            return updatedCart;
        });

        if (itemsCount > 0) {
            setAddedToast({
                name: `${itemsCount} product(s)`,
                quantity: totalItemsAdded
            });
        }

        return itemsCount;
    };

    const addToCart = (product, quantity = 1, selectedAttributes = null, variation = null) => {
        return addBatchToCart([{ product, quantity, selectedAttributes, variation }]);
    };

    const removeFromCart = (productId) => {
        saveCart(cartItems.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(productId);
            return;
        }
        
        const item = cartItems.find(i => i.id === productId);
        if (item) {
            const parsedStock = (item.stock !== undefined && item.stock !== null && item.stock !== '')
                ? parseInt(item.stock, 10)
                : 999;
            if (!isNaN(parsedStock) && parsedStock > 0 && quantity > parsedStock) {
                alert(getStockNotice(parsedStock));
                return;
            }
        }

        saveCart(cartItems.map(item => 
            item.id === productId ? { ...item, quantity } : item
        ));
    };

    const updateCartItemOptions = (itemIndex, newSelectedAttributes, newVariation = null) => {
        if (itemIndex < 0 || itemIndex >= cartItems.length) return;

        const currentItem = cartItems[itemIndex];
        const baseProductId = currentItem.originalProductId || String(currentItem.id).split('_')[0];

        const currentPrice = newVariation?.price !== undefined && newVariation.price > 0
            ? parseFloat(newVariation.price)
            : (parseFloat(currentItem.originalPrice || currentItem.price) || 0);

        const currentStock = newVariation?.stock !== undefined ? parseInt(newVariation.stock, 10) : currentItem.stock;

        let newCartItemId = baseProductId;
        if (newVariation?.id) {
            newCartItemId = `${baseProductId}_${newVariation.id}`;
        } else if (newSelectedAttributes && Object.keys(newSelectedAttributes).length > 0) {
            const attrKey = Object.entries(newSelectedAttributes).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}:${v}`).join('|');
            newCartItemId = `${baseProductId}_${attrKey}`;
        }

        const itemImage = (newVariation?.image) || (currentItem.images && currentItem.images[0]) || '';

        const updatedNormalized = {
            ...currentItem,
            id: newCartItemId,
            cartItemId: newCartItemId,
            originalProductId: baseProductId,
            price: currentPrice,
            stock: currentStock,
            sku: newVariation?.sku || currentItem.sku || '',
            images: itemImage ? [itemImage, ...(currentItem.images || []).filter(img => img !== itemImage)] : (currentItem.images || []),
            selectedAttributes: newSelectedAttributes || newVariation?.attributes || null,
            variationId: newVariation?.id || null
        };

        const existingOtherIndex = cartItems.findIndex((item, idx) => idx !== itemIndex && (item.cartItemId === newCartItemId || item.id === newCartItemId));

        let updatedItems;
        if (existingOtherIndex !== -1) {
            const existingQty = cartItems[existingOtherIndex].quantity;
            const mergedQty = existingQty + currentItem.quantity;
            updatedItems = cartItems
                .filter((_, idx) => idx !== itemIndex)
                .map((item, idx) => idx === (existingOtherIndex > itemIndex ? existingOtherIndex - 1 : existingOtherIndex)
                    ? { ...item, quantity: mergedQty }
                    : item
                );
        } else {
            updatedItems = cartItems.map((item, idx) => idx === itemIndex ? updatedNormalized : item);
        }

        saveCart(updatedItems);
    };

    const clearCart = () => {
        saveCart([]);
    };

    const cartCount = cartItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    const cartTotal = cartItems.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity, 10) || 0)), 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            addBatchToCart,
            removeFromCart,
            updateQuantity,
            updateCartItemOptions,
            clearCart,
            cartCount,
            cartTotal
        }}>
            {children}

            {/* Added to Cart Toast Banner */}
            {addedToast && (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 99999,
                    background: '#0f172a',
                    color: '#ffffff',
                    padding: '14px 20px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    border: '1px solid #334155',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '0.92rem',
                    boxSizing: 'border-box'
                }}>
                    <span style={{ fontSize: '1.4rem' }}>🛒</span>
                    <div>
                        <div style={{ fontWeight: '700', color: '#10b981' }}>Added to Cart!</div>
                        <div style={{ fontSize: '0.82rem', color: '#cbd5e1', marginTop: '2px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {addedToast.name} (x{addedToast.quantity})
                        </div>
                    </div>
                    <Link
                        to={normalizeAppUrl('/cart')}
                        onClick={() => setAddedToast(null)}
                        style={{
                            marginLeft: '8px',
                            background: '#3b82f6',
                            color: '#ffffff',
                            padding: '6px 14px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        View Cart →
                    </Link>
                    <button
                        onClick={() => setAddedToast(null)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            padding: '0 4px'
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}
        </CartContext.Provider>
    );
};
